import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react';
import { authApi } from '@/shared/lib';
import { Restaurant } from '@/pages/Home/comparison/types';

export type AuthType = 'restaurant' | 'supplier' | 'super_admin' | null;

export interface AuthLocation {
  id: string;
  name?: string | null;
  formatted_address?: string | null;
  address?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface User {
  id: string;
  email: string;
  type: string;
  role: string;
  address: string;
  zip_code: string;
  country_iso: string;
  first_name: string;
  last_name: string;
  business_name: string;
  phone: string;
  avatar_url: string | null;
  status: string;
  /** When true, show first-run help / tips / user guide; clear via user update. */
  is_first_time_login?: boolean;
  info_tips_skipped?: boolean;
  metadata: { city: string };
  supplier_id: string | null;
  supplier: string | null;
  businessName: string | null;
  default_location_id?: string | null;
  location_id?: string | null;
  locations?: AuthLocation[];
}

interface AuthContextType {
  user: User | null;
  restaurant: Restaurant | null;
  accessToken: string | null;
  authType: AuthType;
  isAuthenticated: boolean;
  isLoading: boolean;
  businessName: string | null;
  login: (email: string, password: string) => Promise<Exclude<AuthType, null>>;
  /** Persist session from invite/token auth (or any auth response with tokens + user). */
  establishSession: (payload: {
    accessToken: string;
    refreshToken?: string | null;
    user: User;
  }) => Exclude<AuthType, null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LOGIN_ENDPOINT = import.meta.env.VITE_LOGIN_ENDPOINT || '/api/auth/login';

/** Only explicit super_admin role — never supplier/restaurant/platform/admin. */
const isSuperAdminRole = (role?: string | null): boolean =>
  role === 'super_admin';

const resolveAuthType = (userData: {
  role?: string;
  type?: string;
}): Exclude<AuthType, null> => {
  if (isSuperAdminRole(userData.role)) {
    return 'super_admin';
  }
  if (userData.type === 'supplier') {
    return 'supplier';
  }
  return 'restaurant';
};

const restaurantFromUser = (userData: User): Restaurant | null => {
  if (userData.type !== 'restaurant') {
    return null;
  }
  return {
    id: userData.id,
    email: userData.email,
    user_type: 'restaurant',
    role: userData.role,
    first_name: userData.first_name,
    last_name: userData.last_name,
    business_name: userData.business_name,
    phone: userData.phone,
    avatar_url: userData.avatar_url,
    status: userData.status,
    address: userData.address,
    zipCode: userData.zip_code
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [authType, setAuthType] = useState<AuthType>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');
      const storedAuthType = localStorage.getItem('authType') as AuthType;
      const storedRestaurant = localStorage.getItem('restaurant');

      if (storedToken && storedUser && storedAuthType) {
        try {
          const parsedUser = JSON.parse(storedUser) as User;
          // Never trust a spoofed authType — re-derive from stored role.
          const derivedType = resolveAuthType(parsedUser);
          if (
            storedAuthType === 'super_admin' &&
            !isSuperAdminRole(parsedUser.role)
          ) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('restaurant');
            localStorage.removeItem('authType');
          } else {
            localStorage.setItem('authType', derivedType);
            setAccessToken(storedToken);
            setUser(parsedUser);
            setAuthType(derivedType);
            if (storedRestaurant) {
              setRestaurant(JSON.parse(storedRestaurant) as Restaurant);
            } else if (derivedType === 'restaurant') {
              const derivedRestaurant = restaurantFromUser(parsedUser);
              setRestaurant(derivedRestaurant);
              if (derivedRestaurant) {
                localStorage.setItem(
                  'restaurant',
                  JSON.stringify(derivedRestaurant)
                );
              }
            }
          }
        } catch {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('restaurant');
          localStorage.removeItem('authType');
        }
      }
      setIsLoading(false);
    };

    init();
  }, []);

  const establishSession = useCallback(
    (payload: {
      accessToken: string;
      refreshToken?: string | null;
      user: User;
    }): Exclude<AuthType, null> => {
      const nextAuthType = resolveAuthType(payload.user);
      const derivedRestaurant =
        nextAuthType === 'restaurant' ? restaurantFromUser(payload.user) : null;

      localStorage.setItem('accessToken', payload.accessToken);
      if (payload.refreshToken) {
        localStorage.setItem('refreshToken', payload.refreshToken);
      }
      localStorage.setItem('user', JSON.stringify(payload.user));
      localStorage.setItem('authType', nextAuthType);
      if (derivedRestaurant) {
        localStorage.setItem('restaurant', JSON.stringify(derivedRestaurant));
      } else {
        localStorage.removeItem('restaurant');
      }

      setAccessToken(payload.accessToken);
      setUser(payload.user);
      setAuthType(nextAuthType);
      setRestaurant(derivedRestaurant);
      return nextAuthType;
    },
    []
  );

  const login = useCallback(
    async (
      email: string,
      password: string
    ): Promise<Exclude<AuthType, null>> => {
      setIsLoading(true);
      try {
        const response = await authApi.post(
          LOGIN_ENDPOINT,
          { email, password },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const { accessToken: token, refreshToken, user: userData } =
          response.data;
        return establishSession({
          accessToken: token,
          refreshToken,
          user: userData
        });
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [establishSession]
  );

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('restaurant');
    localStorage.removeItem('authType');
    setAccessToken(null);
    setUser(null);
    setRestaurant(null);
    setAuthType(null);
  }, []);

  const isAuthenticated = !!accessToken;

  return (
    <AuthContext.Provider
      value={{
        user,
        restaurant,
        accessToken,
        authType,
        isAuthenticated,
        isLoading,
        businessName:
          restaurant?.business_name || user?.business_name || null,
        login,
        establishSession,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
